/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import AttendanceLetter from './attendance-letter';
import { isEmpty } from '@ember/utils';
import { action, computed } from '@ember/object';
import ThresholdLevel from 'linced-app/enums/threshold-level';
import { sortBy } from '@linc-technologies/linced-common/utils/arrays';
import { autosaveCreateEditorId } from '@linc-technologies/linced-common/utils/tinymce';
import { processTemplate } from '@linc-technologies/linced-common/utils/post-templates';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';

export default class BulkAttendanceLetter extends AttendanceLetter {
  @tracked dialogOpen = false;
  @tracked
  mode = 'groups';
  @tracked currentGroupIndex = 0;
  @tracked currentLearnerIndex = 0;
  @tracked nominatedSchoolPerson = null;
  @tracked
  isCreating = false;

  @tracked
  letterContentMap;

  @tracked
  followUpDate;

  @tracked
  futureMeetingDate;

  @tracked
  includeFollowUpDate;

  @tracked
  includeFutureMeetingDate;

  @tracked
  includePreviousMeetingDate;

  @tracked
  previousMeetingDate;

  init() {
    super.init(...arguments);
    this.letterContentMap = {};
  }

  // Override editorId to include currentLearner for per-learner autosave keys
  @computed('session.person.id', 'currentLearner.id')
  get editorId() {
    let userId = this.session.person.get('id');
    let learnerId = this.currentLearner ? this.currentLearner.id : null;
    return autosaveCreateEditorId(userId, learnerId, null);
  }

  isDateValid(validations, value) {
    return validations.every(({ validate }) => validate(value));
  }

  getCurrentThresholdValue(thresholds) {
    if (isEmpty(thresholds)) {
      return null;
    }
    let threshold = sortBy(thresholds, 'date').at(-1);
    return threshold ? threshold.threshold : null;
  }

  getResponseActivitiesForLearner(learnerId) {
    if (isEmpty(this.responseActivities)) {
      return [];
    }
    return this.responseActivities.filter((a) => a.person_id === learnerId);
  }

  getLearnerAbsences(learnerId) {
    let termAbsences =
      this.learnerAbsencesByTerm && this.learnerAbsencesByTerm[learnerId];
    if (!termAbsences) {
      return 0;
    }
    return termAbsences[this.currentTermTerm] || 0;
  }

  @computed('selectedLearners.[]', 'currTermThresholdsMap')
  get thresholdGroups() {
    let groups = {};
    this.selectedLearners.forEach((learner) => {
      let thresholds =
        this.currTermThresholdsMap && this.currTermThresholdsMap[learner.id];
      let thresholdValue = this.getCurrentThresholdValue(thresholds);
      if (isEmpty(thresholdValue)) {
        return;
      }
      if (!groups[thresholdValue]) {
        groups[thresholdValue] = {
          thresholdValue,
          thresholdLabel: ThresholdLevel.getLabel(thresholdValue),
          learners: [],
        };
      }
      groups[thresholdValue].learners.push(learner);
    });
    return Object.values(groups).sort(byKeys('thresholdValue'));
  }

  @computed('thresholdGroups', 'currentGroupIndex')
  get currentGroup() {
    return this.thresholdGroups[this.currentGroupIndex] || null;
  }

  get isFirstGroup() {
    return this.currentGroupIndex === 0;
  }

  @computed('currentGroupIndex', 'thresholdGroups')
  get isLastGroup() {
    return this.currentGroupIndex >= this.thresholdGroups.length - 1;
  }

  @computed('thresholdGroups')
  get eligibleCount() {
    return this.thresholdGroups.reduce((sum, g) => sum + g.learners.length, 0);
  }

  get currentGroupDisplayIndex() {
    return this.currentGroupIndex + 1;
  }

  @computed('currentGroup', 'currentLearnerIndex')
  get currentLearner() {
    if (!this.currentGroup) {
      return null;
    }
    return this.currentGroup.learners[this.currentLearnerIndex] || null;
  }

  get isFirstLearner() {
    return this.currentLearnerIndex === 0;
  }

  @computed('currentLearnerIndex', 'currentGroup')
  get isLastLearner() {
    if (!this.currentGroup) {
      return true;
    }
    return this.currentLearnerIndex >= this.currentGroup.learners.length - 1;
  }

  get currentLearnerDisplayIndex() {
    return this.currentLearnerIndex + 1;
  }

  @computed(
    'nominatedSchoolPerson',
    'currentGroup',
    'followUpDate',
    'futureMeetingDate',
    'previousMeetingDate',
    'includeFollowUpDate',
    'includeFutureMeetingDate',
    'includePreviousMeetingDate',
  )
  get currentGroupValid() {
    if (isEmpty(this.nominatedSchoolPerson) || !this.currentGroup) {
      return false;
    }
    switch (this.currentGroup.thresholdValue) {
      case ThresholdLevel.RED:
        return (
          !this.includeFollowUpDate ||
          this.isDateValid(this.followUpDateValidations, this.followUpDate)
        );
      case ThresholdLevel.YELLOW:
        return (
          !this.includeFutureMeetingDate ||
          this.isDateValid(
            this.futureMeetingDateValidations,
            this.futureMeetingDate,
          )
        );
      case ThresholdLevel.ORANGE:
        return (
          !this.includePreviousMeetingDate ||
          this.isDateValid(
            this.previousMeetingDateValidations,
            this.previousMeetingDate,
          )
        );
    }
    return true; // GREEN needs no date, just nominated person
  }

  @computed(
    'currentLearner',
    'nominatedSchoolPerson',
    'dialogOpen',
    'followUpDate',
    'futureMeetingDate',
    'previousMeetingDate',
    'includeFollowUpDate',
    'includeFutureMeetingDate',
    'includePreviousMeetingDate',
  )
  get currentLetterContent() {
    if (!this.dialogOpen) {
      return '';
    }
    let learner = this.currentLearner;
    if (!learner || !this.currentGroup) {
      return '';
    }
    // letterContentMap is not a listed dependency — direct mutations from
    // updateLetterContent never cause a recompute / TinyMCE re-render.
    let stored = this.letterContentMap[learner.id];
    if (!isEmpty(stored)) {
      return stored;
    }
    let template = this.getAttendanceTemplate(this.currentGroup.thresholdValue);
    let responseActivities = this.getResponseActivitiesForLearner(learner.id);
    let absences = this.getLearnerAbsences(learner.id);
    let context = this.getAttendanceContext(
      learner,
      absences,
      responseActivities,
    );
    return processTemplate(template, {
      school: this.get('session.school'),
      person: learner,
      attendance: context,
    });
  }

  @action
  openDialog() {
    this.dialogOpen = true;
    this.currentGroupIndex = 0;
    this.currentLearnerIndex = 0;
    this.mode = 'groups';
  }

  @action
  closeDialog() {
    this.dialogOpen = false;
    this.currentGroupIndex = 0;
    this.currentLearnerIndex = 0;
    this.mode = 'groups';
    this.nominatedSchoolPerson = null;
    this.letterContentMap = {};
    this.followUpDate = null;
    this.futureMeetingDate = null;
    this.previousMeetingDate = null;
    this.includeFollowUpDate = true;
    this.includeFutureMeetingDate = true;
    this.includePreviousMeetingDate = true;
  }

  @action
  nextGroup() {
    if (this.isLastGroup) {
      this.mode = 'review';
    } else {
      this.currentGroupIndex = this.currentGroupIndex + 1;
      this.currentLearnerIndex = 0;
    }
  }

  @action
  prevGroup() {
    if (this.mode === 'review') {
      this.mode = 'groups';
      this.currentLearnerIndex = 0;
    } else if (!this.isFirstGroup) {
      this.currentGroupIndex = this.currentGroupIndex - 1;
      this.currentLearnerIndex = 0;
    }
  }

  @action
  nextLearner() {
    this.currentLearnerIndex = this.currentLearnerIndex + 1;
  }

  @action
  prevLearner() {
    this.currentLearnerIndex = this.currentLearnerIndex - 1;
  }

  // Called by TinyMCE onChange — stores edited content per learner without
  // feeding it back through currentLetterContent (which would force TinyMCE
  // to re-render and lose cursor position).
  @action
  updateLetterContent(value) {
    let learner = this.currentLearner;
    if (!learner) {
      return;
    }
    this.letterContentMap[learner.id] = value;
  }

  @action
  removeNominatedPerson() {
    this.nominatedSchoolPerson = null;
    this.letterContentMap = {};
  }

  @action
  setNominatedPerson(person) {
    this.nominatedSchoolPerson = person;
  }

  @action
  updateDate(field, value) {
    this.set(field, value);
  }

  @action
  toggleGroupDateField(field, value) {
    this.set(field, value);
    // Clear cached letters for this group so the template regenerates
    if (this.currentGroup) {
      this.currentGroup.learners.forEach((learner) => {
        delete this.letterContentMap[learner.id];
      });
    }
  }

  @action
  createAllLetters() {
    if (isEmpty(this.attendancePage)) {
      this.toaster.error(
        'No attendance page found. Please create an attendance page first.',
      );
      return;
    }
    this.isCreating = true;

    let promises = [];

    this.thresholdGroups.forEach((group) => {
      group.learners.forEach((learner) => {
        // Use the stored content (possibly user-edited) if it exists,
        // otherwise generate fresh from the template.
        let content = this.letterContentMap[learner.id];
        if (!content) {
          let template = this.getAttendanceTemplate(group.thresholdValue);
          let responseActivities = this.getResponseActivitiesForLearner(
            learner.id,
          );
          let absences = this.getLearnerAbsences(learner.id);
          let context = this.getAttendanceContext(
            learner,
            absences,
            responseActivities,
          );
          content = processTemplate(template, {
            school: this.get('session.school'),
            person: learner,
            attendance: context,
          });
        }
        let post = this.store.createRecord('posts/post', {
          postType: PostType.POST,
          status: PostStatus.DRAFT,
          schoolId: this.get('session.school'),
          authorId: this.get('session.person'),
          personId: learner,
          pageId: this.attendancePage,
          content,
          subject: 'Attendance Notice',
        });
        promises.push(post.save());
      });
    });

    let label = promises.length === 1 ? 'Letter' : 'Letters';
    Promise.all(promises)
      .then(() => {
        this.toaster.success(
          `${promises.length} Attendance ${label} created successfully.`,
        );
      })
      .catch(() => {
        this.toaster.error(`Error creating attendance ${label}.`);
      })
      .finally(() => {
        this.closeDialog();
        this.isCreating = false;
      });
  }
}
